<script lang="ts">
  import { cn } from "$lib/utils";
  import { Button } from "$lib/components/ui/button";
  import * as Tooltip from "$lib/components/ui/tooltip";
  import { Pencil, BadgeCheck, User, Building2, Dot } from "lucide-svelte";
  import OpenAiStatusMonitor from "$lib/components/monitors/openAIStatusMonitor.svelte";
  import type { SearchInfo, SearchProgress } from "$lib/types";
  import { SEARCH_PROGRESS } from "$lib/types";
  import { searchPreferences, authStore, searchStore, listStore } from "$lib/stores";
  import { createEventDispatcher } from "svelte";
  import AddCriterionDialog from "$lib/components/dialogs/addCriterionDialog.svelte";
  import ConvertToListButton from "./convertToListButton.svelte";
  import type { List } from "$lib/types";
  import { page } from "$app/state";
  import { searchFormOverlayStore } from "$lib/stores/searchFormOverlayStore";

  let {
    subscriptionDialogOpen = $bindable(false),
    recordType = "search",
    isViewedByOwner,
    searchQuery = "",
    searchCriteria = [],
    definiteMatchesCount,
    partialMatchesCount,
    localSearchProgress,
    filteredResultsLength,
    isLoadingMoreResults,
    showDefiniteOnly,
    className = "",
  } = $props<{
    recordType: "search" | "list";
    subscriptionDialogOpen: boolean;
    isViewedByOwner: boolean;
    searchQuery: string;
    searchCriteria?: string[];
    definiteMatchesCount: number;
    partialMatchesCount: number;
    localSearchProgress: string;
    filteredResultsLength: number;
    isLoadingMoreResults: boolean;
    showDefiniteOnly: boolean;
    className?: string;
  }>();

  function handleShowOverlay() {
    logger.log("Show overlay", searchQuery, searchCriteria);
    if ($authStore.isLoggedIn && $authStore.user?.plan !== "free") {
      searchPreferences.setProModeValue({ value: true });
    }
    searchFormOverlayStore.show(searchQuery, searchCriteria);
    searchFormOverlayStore.show(searchQuery, searchCriteria);
  }

  let currentSearch = $derived(recordType == "search" ? $searchStore[page.params.slug] : $listStore[page.params.slug]);

  const dispatch = createEventDispatcher();

  const hasFilteredResults = $derived(filteredResultsLength > 0);
  const hasDefiniteMatches = $derived(definiteMatchesCount > 0);
  const hasPartialMatches = $derived(partialMatchesCount > 0);
  const canShowAnalyzingMatches = $derived(
    "searchStatus" in currentSearch &&
      (currentSearch?.searchStatus === "processing" || currentSearch?.searchStatus === "expanding"),
  );
  const isProSearch = $derived("mode" in currentSearch && currentSearch?.mode === "pro");
  const isProSearchFinished = $derived(
    "searchStatus" in currentSearch &&
      isProSearch &&
      !["expanding", "processing"].includes(currentSearch.searchStatus as string),
  );

  const searchStatusMessage = $derived(getSearchStatusMessage(currentSearch, localSearchProgress));

  function getSearchStatusMessage(search: SearchInfo | List | undefined, localSearchProgress: SearchProgress): string {
    if (!search) return "";

    if (search.error) {
      return `${search.error}`;
    }

    if (!search.category) {
      return "Initiating search...";
    }

    if (
      isLoadingMoreResults ||
      localSearchProgress === SEARCH_PROGRESS.IN_PROGRESS ||
      ("searchStatus" in search && (search.searchStatus === "processing" || search.searchStatus === "expanding"))
    ) {
      return isProSearch ? "Analyzing with Pro AI Model..." : "Analyzing...";
    }

    return "";

    // switch (search.progressStatus) {
    //   case SEARCH_PROGRESS.NOT_STARTED:
    //     return "Initiating search...";
    //   default:
    //     return isProSearch ? "Analyzing with Pro AI Model..." : "Analyzing...";
    // }
  }

  // function handleCSVDownload(): Promise<void> {
  //   return new Promise((resolve) => {
  //     dispatch("csvDownload");
  //     resolve();
  //   });
  // }

  // function handlePremiumCSVDownload(searchId: number, userId: number): Promise<void> {
  //   return new Promise((resolve) => {
  //     dispatch("premiumCsvDownload");
  //     resolve();
  //   });
  // }
</script>

<div
  class={cn(
    "search-header relative w-full",
    "flex items-center justify-between",
    "bg-theme-background-primary dark:bg-theme-background-tertiary",
    "px-5 pt-0 text-left md:px-4",
    $authStore.isLoggedIn ? "top-0" : "top-18 md:top-0",
    className,
  )}
>
  <OpenAiStatusMonitor wrapperClass="lg:absolute top-3 right-3 z-40 mt-3 mb-4.5 lg:mt-0 lg:mb-0" />

  <h1 class="relative mt-2 text-2xl font-bold leading-tight">
    <span class="inline-flex items-center align-middle">
      {#if isProSearch}
        <span
          class="mr-3 inline-flex items-center rounded-base border-1 border-black bg-theme-green px-2 py-0.5 text-sm font-semibold text-black"
        >
          Pro
        </span>
      {/if}
      <span
        class={cn(
          "transition-default mr-1 inline-flex h-5.5 items-center bg-black/90 px-1 align-middle text-white dark:bg-white dark:text-black",
          currentSearch?.category === "person" ? "rounded-full" : "rounded-base",
        )}
      >
        {#if currentSearch?.category === "person"}
          <User class="h-3.5 w-3.5" strokeWidth={1.5} />
        {:else if currentSearch?.category === "company"}
          <Building2 class="h-3.5 w-3.5" strokeWidth={1.5} />
        {:else}
          <Dot class="h-3.5 w-3.5 animate-pulse" strokeWidth={1.5} />
        {/if}
      </span>
    </span>
    <span class="align-middle">{searchQuery}</span>

    {#if $authStore.isLoggedIn && isViewedByOwner}
      {#if recordType == "search"}
        <Tooltip.Provider>
          <Tooltip.Root delayDuration={125}>
            <Tooltip.Trigger>
              <Button
                variant="ghost"
                class={cn(
                  "!h-6 !w-6 !rounded-base p-0 align-middle",
                  "dark:bg-theme-background-tertiary",
                  "dark:text-theme-gray-600",
                  "dark:border-white/10",
                  "dark:hover:bg-theme-background-secondary",
                  "dark:hover:text-theme-gray-800",
                  "dark:hover:border-white/20",
                )}
                aria-label="Edit search"
                onclick={() => handleShowOverlay()}
              >
                <Pencil size={24} strokeWidth={1.5} />
              </Button>
            </Tooltip.Trigger>
            <Tooltip.Content
              class={cn(
                "!rounded-base px-2 py-1 text-2xs font-normal",
                "dark:bg-theme-background-tertiary",
                "dark:text-theme-gray-600",
                "dark:border dark:border-white/10",
              )}
              side="bottom"
            >
              <p>Edit Search</p>
            </Tooltip.Content>
          </Tooltip.Root>
        </Tooltip.Provider>
      {/if}
    {/if}
  </h1>

  {#if $authStore.isLoggedIn}
    {#if "algoVersion" in currentSearch && currentSearch.algoVersion}
      <span
        class="absolute right-3.5 top-3 hidden items-center bg-theme-background-primary text-xs font-normal lg:inline-flex"
      >
        {searchPreferences.getPublicSearchLabel(currentSearch.algoVersion)}
      </span>
    {/if}
  {/if}

  <!-- {#if searchQuery} -->
  <!-- <div class="flex items-center gap-2">
      {#if currentSearch?.id && isViewedByOwner}
        <ShareButton searchId={currentSearch.id} isPublic={currentSearch.isPublic || false} />
      {/if}

      {#if currentSearch && currentSearch?.id && currentSearch.results?.length && isViewedByOwner}
        <ConvertToListButton {currentSearch} {isViewedByOwner} />

        <div>
          <ExportDialog
            category={currentSearch?.category}
            creditsRequired={CREDITS_PER.EMAIL * currentSearch?.results.length}
            creditsAvailable={$authStore.user?.credits?.total || 0}
            onBasicExport={() => handleCSVDownload()}
            onPremiumExport={() => handlePremiumCSVDownload(Number(currentSearch?.id), Number($authStore.user?.id))}
            disabled={localSearchProgress === SEARCH_PROGRESS.IN_PROGRESS}
            loggedOutCallback={() => (dialogOpen = true)}
            isProcessingCSV={isExportingPremiumCSV}
          >
            <Button
              variant="outline"
              class="justift-center flex h-fit items-center gap-1.5 !rounded-base px-1.5 py-1 text-2xs font-medium text-theme-text-primary dark:border-white dark:border-white/20 dark:bg-theme-background-tertiary dark:hover:border-theme-text-secondary dark:hover:text-theme-text-primary"
              aria-label="Export data"
              disabled={localSearchProgress === SEARCH_PROGRESS.IN_PROGRESS}
            >
              <FileDown strokeWidth={1} class="max-h-4" />
              <span class="font-normal">Export</span>
            </Button>
          </ExportDialog>
        </div>
      {/if}
    </div> -->

  <!-- Results count and status -->

  <!-- Criteria section -->
  <!-- {#if currentSearch?.criteria?.length} -->
  <!-- <div class="mt-2 text-xs">
        <div class="flex items-center gap-0">
          <span class="flex items-center font-bold dark:text-theme-gray-700"> Criteria </span> -->

  <!-- {#if !isExpanded}
            &nbsp;
            <span class="flex gap-0">
              {#each (currentSearch?.criteria || []).slice().sort((a: string, b: string) => (criteriaToggles[b] ?? false ? 1 : 0) - (criteriaToggles[a] ?? false ? 1 : 0)) as criteria}
                <span class={cn("text-[10px]", criteriaToggles[criteria] === true
                ? "text-theme-green"
                : "text-theme-gray-600 dark:text-theme-gray-400",)}>■</span>
              {/each}
            </span>
          {/if} -->

  <!-- <Button
            variant="ghost"
            size="sm"
            class={cn(
              "!rounded-base h-6 w-6 p-0 ml-1",
              "dark:bg-theme-background-tertiary",
              "dark:text-theme-gray-600",
              "dark:border-white/10",
              "dark:hover:bg-theme-background-secondary",
              "dark:hover:text-theme-gray-800",
              "dark:hover:border-white/20",
            )}
            onclick={() => (isExpanded = !isExpanded)}
          >
            {#if isExpanded}
              <ChevronUp size={3} strokeWidth={1.5} />
            {:else}
              <ChevronDown size={3} strokeWidth={1.5} />
            {/if}
          </Button> -->
  <!-- </div> -->

  <!-- Expanding/collapsing criteria list -->
  <!-- <div class={cn("overflow-hidden transition-all duration-200", isExpanded ? "mt-1 max-h-96" : "max-h-0")}>
          <div class="flex flex-col flex-wrap items-start gap-0 dark:text-theme-gray-700">
            <div class="mb-1 flex gap-1">
              {#if isViewedByOwner}
                <Button
                  variant="outline"
                  class={cn(
                    "flex h-fit items-center gap-1.5 px-1.5 py-0.5",
                    "text-2xs font-normal",
                    "focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0",
                    "!rounded-base",
                    "transition-colors",
                    "dark:border-white/20",
                    "dark:bg-theme-background-tertiary dark:text-theme-gray-600 dark:hover:border-theme-text-secondary dark:hover:text-theme-gray-800",
                  )}
                  onclick={handleAllCriteriaToggle}
                >
                  <span class="flex items-center gap-0.5">
                    <Check strokeWidth={1.5} class="max-h-3" />
                    Toggle all
                  </span>
                </Button>
              {/if}
              {#if isViewedByOwner}
                <AddCriterionDialog
                  onAddCriterion={(criterion) => {
                    temporaryCriteria = [...temporaryCriteria, criterion];
                  }}
                />

                {#if showRerunButton}
                  <Button
                    variant="outline"
                    class={cn(
                      "flex h-fit items-center gap-1.5 px-1.5 py-0.5",
                      "text-2xs font-normal",
                      "focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0",
                      "!rounded-base",
                      "transition-colors",
                      "dark:border-white/20",
                      "dark:bg-theme-background-tertiary dark:text-theme-gray-600 dark:hover:border-theme-text-secondary dark:hover:text-theme-gray-800",
                    )}
                    onclick={() => {
                      const uniqueCriteria = Array.from(new Set([...currentSearch.criteria, ...temporaryCriteria]));

                      dispatch("rerunSearch", {
                        criteria: uniqueCriteria,
                      });

                      // Clear temporary criteria
                      temporaryCriteria = [];
                    }}
                  >
                    <span class="flex items-center gap-0.5">
                      <RefreshCcw strokeWidth={1.5} class="max-h-3" />
                      Re-run search
                    </span>
                  </Button>
                {/if}
              {/if}
            </div>

            {#each [...currentSearch.criteria, ...temporaryCriteria].sort((a, b) => {
              // Handle both object and string criteria
              const aText = typeof a === "object" && a !== null ? a.fullDescription : String(a);
              const bText = typeof b === "object" && b !== null ? b.fullDescription : String(b);

              const aIsTemp = temporaryCriteria.includes(aText);
              const bIsTemp = temporaryCriteria.includes(bText);

              if (aIsTemp !== bIsTemp) {
                return aIsTemp ? 1 : -1;
              }

              return aText.localeCompare(bText);
            }) as criteria}
              <div class="flex items-start justify-start gap-1">
                <Tooltip.Provider>
                  <Tooltip.Root>
                    <Tooltip.Trigger>
                      <button
                        type="button"
                        class={cn(
                          "cursor-pointer",
                          "focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-offset-0",
                          "!focus-visible:ring-0 !focus-visible:ring-offset-0",
                        )}
                        onclick={() =>
                          toggleCriterion(typeof criteria === "object" ? criteria.fullDescription : criteria)}
                        onkeydown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleCriterion(typeof criteria === "object" ? criteria.fullDescription : criteria);
                          }
                        }}
                        aria-label={`Toggle criterion: ${typeof criteria === "object" ? criteria.fullDescription : criteria}`}
                      >
                        <span
                          class={cn(
                            "pb-0.5 align-middle text-[7px] leading-[7px]",
                            criteriaToggles[typeof criteria === "object" ? criteria.fullDescription : criteria] === true
                              ? "text-theme-green"
                              : "text-theme-text-secondary dark:text-theme-gray-400",
                          )}>■</span
                        >
                        <span
                          class={cn(
                            "text-theme-text-primary dark:text-theme-text-secondary dark:hover:text-theme-text-primary",
                          )}
                          >{(typeof criteria === "object" ? criteria.fullDescription : criteria).replace(
                            /\.$/,
                            "",
                          )}</span
                        >
                      </button>
                    </Tooltip.Trigger>
                    <Tooltip.Content
                      side="bottom"
                      class={cn(
                        "rounded-base px-2 py-1 text-xs font-normal",
                        "dark:bg-theme-background-tertiary",
                        "dark:text-theme-gray-600",
                        "dark:border dark:border-white/10",
                      )}
                    >
                      <p>
                        {@html criteriaToggles[criteria] === true
                          ? "Click to make this criteria <string class='font-medium text-theme-text-primary'>optional</string>. authStore value: required."
                          : "Click to make this criteria <strong class='font-medium text-theme-text-primary'>required</strong>. authStore value: optional."}
                      </p>
                    </Tooltip.Content>
                  </Tooltip.Root>
                </Tooltip.Provider>

                {#if temporaryCriteria.includes(criteria)}
                  <Button
                    variant="outline"
                    size="sm"
                    class={cn(
                      "h-4 w-4 p-0",
                      "hover:bg-red-100 dark:hover:bg-red-900/20",
                      "focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-offset-0",
                    )}
                    onclick={() => {
                      temporaryCriteria = temporaryCriteria.filter((c) => c !== criteria);
                      currentSearch.criteria = currentSearch.criteria.filter((c: string) => c !== criteria);
                    }}
                  >
                    <Minus class="h-2 w-2" />
                  </Button>
                {/if}
              </div>
            {/each}
          </div>
        </div> -->
  <!-- </div> -->
  <!-- {/if} -->
  <!-- {/if} -->
</div>

<div class="mt-0 flex flex-col gap-3 px-6 pt-0 text-left sm:flex-row md:px-4">
  <!-- <span class="text-xs font-bold dark:text-theme-gray-700">Status</span> -->
  <div class="bullet-separated items-start text-left text-xs leading-5 dark:text-theme-text-secondary">
    {#if localSearchProgress === SEARCH_PROGRESS.NOT_STARTED}
      <span class="dark:text-theme-text-secondary">{searchStatusMessage}</span>
    {:else if hasFilteredResults}
      {#if hasDefiniteMatches}
        <span class="dark:text-theme-text-secondary">
          {definiteMatchesCount} definite {definiteMatchesCount === 1 ? "match" : "matches"}
        </span>
      {/if}
      {#if hasPartialMatches}
        <span class={cn("dark:text-theme-text-secondary", showDefiniteOnly ? "opacity-20" : "")}>
          {partialMatchesCount} partial {partialMatchesCount === 1 ? "match" : "matches"}
        </span>
      {/if}
      {#if canShowAnalyzingMatches}
        <span class="dark:text-theme-text-secondary">
          {searchStatusMessage}
        </span>
      {/if}
      {#if "mode" in currentSearch && currentSearch && currentSearch.mode == "default"}
        <span class="h-fit w-fit text-nowrap rounded-full bg-theme-green/80 px-2 py-0.5 text-xs font-normal text-black">
          <a
            href="#toggle-subscription-prompt"
            onclick={(e) => {
              e.preventDefault();
              if (!$authStore.isLoggedIn || $authStore.user?.plan == "free") {
                subscriptionDialogOpen = true;
              }
            }}
            >try <span class="font-bold">pro search</span> to get additional and more accurate results
          </a>
        </span>
      {/if}
      {#if isProSearchFinished}
        <span class="inline-flex items-center justify-center gap-1 dark:text-theme-text-secondary">
          Verified with Pro AI Model
          <BadgeCheck
            strokeWidth={1.5}
            size={16}
            class="fill-theme-gray-800 text-theme-background-primary dark:fill-theme-gray-600 dark:text-theme-background-primary"
          />
        </span>
      {/if}
    {:else}
      <span class="dark:text-theme-text-secondary">{searchStatusMessage}</span>
    {/if}
  </div>
</div>
